// src/products/dataroom/components/Modals/ProjectCreationModal.js
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const ProjectCreationModal = ({ isOpen, onClose, onProjectCreated, user, selectedTemplate, setShowTemplateModal }) => {
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [urlName, setUrlName] = useState('');

  const handleCreate = () => {
    onProjectCreated(projectName, projectDescription, selectedTemplate, urlName);
    // Reset form
    setProjectName('');
    setProjectDescription('');
    setUrlName('');
  };

  const handleClose = () => {
    setProjectName('');
    setProjectDescription('');
    setUrlName('');
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && projectName.trim()) {
      handleCreate();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center border-b pb-3 mb-4">
          <h3 className="text-xl font-semibold text-gray-800">Create New Project</h3>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>
        <label htmlFor="projectName" className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
        <input
          type="text"
          id="projectName"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., Series A Fundraising"
        />
        <label htmlFor="projectDescription" className="block text-sm font-medium text-gray-700 mt-4 mb-2">Description (optional)</label>
        <textarea
          id="projectDescription"
          value={projectDescription}
          onChange={(e) => setProjectDescription(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows="3"
          placeholder="Enter project description"
        />
        <label htmlFor="urlName" className="block text-sm font-medium text-gray-700 mt-4 mb-2">
          Dealroom URL Name <span className="text-gray-500">(optional)</span>
        </label>
        <div className="space-y-1">
          <input
            type="text"
            id="urlName"
            value={urlName}
            onChange={(e) => {
              // Only allow alphanumeric, max 20 chars
              const cleaned = e.target.value.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
              setUrlName(cleaned);
            }}
            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            placeholder="mydealroom"
            maxLength={20}
          />
          <p className="text-xs text-gray-500">
            Letters and numbers only, max 20 characters. A unique 5-character token will be added automatically.
            {urlName && (
              <span className="block mt-1 text-blue-600 font-mono">
                URL will be: /dealroom/{urlName}-xxxxx
              </span>
            )}
          </p>
        </div>
        <button
          onClick={() => setShowTemplateModal(true)}
          className="mt-4 bg-green-500 text-white px-4 py-2 rounded flex items-center"
        >
          Choose Template
        </button>
        {selectedTemplate && (
          <p className="mt-2 text-sm text-gray-600">Selected: {selectedTemplate.name}</p>
        )}
        <div className="flex justify-end mt-6 space-x-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 bg-gray-200 rounded-full hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!projectName.trim()}
            className="px-4 py-2 text-white bg-blue-600 rounded-full hover:bg-blue-700 disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProjectCreationModal;